import { useInfiniteQueryDeliveriesList, UseQueryListDeliveriesParams } from '../../deliveries.queries';
import { useQueryListCarriers } from '../../../carriers/carriers.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { useQueryGetProject, useQueryGetProjectChildProjects } from '../../../../core/queries/projects.queries';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { KnownHubTypeCountDtoType, KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useTimelineUpdatesEventCentre } from '../../../../events/timelineUpdates/useTimelineUpdatesEventCentre.hook';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TimelineUpdatesSubscriptionMatcher,
  TimelineUpdatesSubscriptionCallback,
  TimelineUpdatesSubscriptionStream
} from '../../../../events/timelineUpdates/timelineUpdates.types';
import { useQueryHubSummary } from '../../../lockers/hubs.queries';
import { useHubFilter } from '../../../lockers/hooks/use-hub-filter.hook';
import { DeliveriesListActions } from '../actions/deliveries-list.actions';
import dayjs from 'dayjs';
import {
  liveRefreshIntervalForProjectType,
  useThrottledFirstPageRefetch
} from '../../../../hooks/use-throttled-first-page-refetch.hook';

const initialControlsState: UseQueryListDeliveriesParams = {
  state: '',
  deliveryType: '',
  searchTerm: '',
  deliveredFrom: null,
  deliveredTo: null,
  createdFrom: dayjs().subtract(3, 'month').startOf('day'),
  createdTo: null,
  hubID: null,
  carrierID: null,
  sortBy: 'createdTime',
  sortOrder: KnownSortOrder.Desc
};

const searchParamOptions: UseSearchParamsOptions<UseQueryListDeliveriesParams> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true,
    parseDates: ['deliveredFrom', 'deliveredTo', 'createdFrom', 'createdTo']
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

interface DeliveriesListPageState {
  currentAction: DeliveriesListActions | null;
}

const initialState: DeliveriesListPageState = {
  currentAction: null
};

export function useDeliveriesList(projectID: string) {
  const [state, setState] = useState<DeliveriesListPageState>(initialState);

  function setAction(action: DeliveriesListActions | null) {
    setState((state) => ({ ...state, currentAction: action }));
  }

  const endAction = useCallback((nextAction?: DeliveriesListActions) => {
    setAction(nextAction || null);
  }, []);

  const handlers = useMemo(
    () => ({
      setAction,
      endAction
    }),
    [endAction]
  );

  const project = useQueryGetProject(projectID);
  const childProjects = useQueryGetProjectChildProjects(projectID);
  const [formData, queryData, setFormData] = useSearchParams(initialControlsState, searchParamOptions);
  const deliveries = useInfiniteQueryDeliveriesList(projectID, 1, queryData);
  const hubSummary = useQueryHubSummary(projectID, { includeChildProjects: true });
  const clearHub = useCallback(() => setFormData({ hubID: null }), [setFormData]);
  const hubFilter = useHubFilter(projectID, formData.hubID, clearHub);
  // Loaded when the filter opens, or straight away if one is already selected, so it can be named.
  const [carriersRequested, setCarriersRequested] = useState(false);
  const carriers = useQueryListCarriers(projectID, { enabled: carriersRequested || !!formData.carrierID });
  const requestCarriers = useCallback(() => setCarriersRequested(true), []);

  const projectIDsInScope = useMemo(() => (childProjects.data || []).map((p) => p.id), [childProjects.data]);

  // subscribe to timeline update events
  const refetchFirstPage = useThrottledFirstPageRefetch(
    deliveries.refetch,
    liveRefreshIntervalForProjectType(project.data?.projectType)
  );
  const callback: TimelineUpdatesSubscriptionCallback = useCallback(() => {
    refetchFirstPage();
  }, [refetchFirstPage]);

  const matcher: TimelineUpdatesSubscriptionMatcher = useCallback(
    (event) => {
      if (event.data.projectID && projectIDsInScope.includes(event.data.projectID)) {
        return true;
      }
      return false;
    },
    [projectIDsInScope]
  );

  const stream: TimelineUpdatesSubscriptionStream = useMemo(
    () => ({
      deliveryIDs: [],
      workflowIDs: [],
      projectIDs: projectIDsInScope
    }),
    [projectIDsInScope]
  );

  const subscription = useTimelineUpdatesEventCentre({ callback, matcher, stream });

  // Pause the subscription when the search term is not empty.
  // This is to prevent the subscription from refetching data when the user is searching.
  const isSearchTerm = useMemo(() => !!queryData.searchTerm, [queryData.searchTerm]);
  useEffect(() => {
    if (isSearchTerm) {
      subscription.pause();
    } else {
      subscription.resume();
    }
  }, [isSearchTerm, subscription]);

  const carrierOptions = useMemo(
    () =>
      (carriers.data?.items || []).map((c) => ({
        label: c.name,
        value: c.id
      })),
    [carriers.data]
  );

  // If the Project selector is changed, the list of Carriers might change.
  // If the selected carrier is not in the list of options, set the selected carrier to null
  useEffect(() => {
    if (!carriers.isSuccess) {
      return;
    }
    if (!formData.carrierID) {
      return;
    }
    const selectedOption = carrierOptions.find((o) => o.value === formData.carrierID);
    if (!selectedOption) {
      setFormData({ carrierID: null });
    }
  }, [carriers.isSuccess, formData.carrierID, carrierOptions, setFormData]);

  const showHubNameInTable = (hubSummary.data?.count ?? 0) > 1;

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: deliveries.hasNextPage || false,
    isFetchingNextPage: deliveries.isFetchingNextPage,
    fetchNextPage: deliveries.fetchNextPage,
    onScrollToBottom() {
      deliveries.fetchNextPage();
    }
  };

  const showReceiveDeliveryButton =
    hubSummary.data?.types.some((t) => t.type === KnownHubTypeCountDtoType.KiosklessSpacesHub) ?? false;

  return {
    project,
    deliveries,
    infiniteScroll,
    formData,
    setFormData,
    hubFilter,
    carrierFilter: { options: carrierOptions, loading: carriers.isLoading, onOpen: requestCarriers },
    showHubNameInTable,
    state,
    handlers,
    showReceiveDeliveryButton
  } as const;
}
